/** @jsxImportSource @emotion/react */
import React, { useCallback } from 'react'
import { TeamOutlined } from '@ant-design/icons'
import {
  MyTeamOfficial,
  UpcomingGame,
} from '@sportninja/common/types/PlayerAttendance'
import {
  manageAttendanceButtonStyle,
  manageAttendanceTextStyle,
  styledTeamOutlinedStyle,
} from '../styles'
import { trackEvent, getPlatformType } from '../../../utils/analytics'

/**
 * Props for the ManageTeamAttendance component
 */
export interface ManageTeamAttendanceProps {
  /** Team official records to render manage buttons for */
  teamRepsAttendance: MyTeamOfficial[]
  /** Game data for team name resolution */
  rowItem: UpcomingGame
  /** Whether the user is playing on the home team */
  isPlayingOnHomeTeam: boolean
  /** Callback when manage attendance button is pressed */
  onPressManageAttendance: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
}

/**
 * Renders "Manage Team Attendance" buttons for each team the user is a team rep on.
 * Shows team name suffix when the user is a rep on multiple teams in the same game.
 *
 * @param {ManageTeamAttendanceProps} props - Component props
 * @returns {JSX.Element} Manage attendance button(s)
 */
const ManageTeamAttendance: React.FC<ManageTeamAttendanceProps> = ({
  teamRepsAttendance,
  rowItem,
  isPlayingOnHomeTeam,
  onPressManageAttendance,
}) => {
  const handlePress = useCallback(
    (teamRep: MyTeamOfficial) => {
      const teamSide =
        teamRep?.team_id === rowItem?.home_team?.id ? 'home' : 'visitor'
      trackEvent('home_game_card_manage_attendance_tapped', {
        platform: getPlatformType(),
        game_id: rowItem?.id,
        team_side: teamSide,
      })

      const teamNameForNavigation =
        teamRepsAttendance.length > 1
          ? teamRep?.team_id === rowItem?.home_team?.id
            ? `${rowItem?.home_team?.name}`
            : `${rowItem?.visiting_team?.name}`
          : isPlayingOnHomeTeam
          ? rowItem?.home_team?.name
          : rowItem?.visiting_team?.name

      onPressManageAttendance(
        rowItem?.id,
        teamRep?.game_player_roster_id,
        teamRep?.team_id,
        teamNameForNavigation,
        rowItem?.show_waiver
      )
    },
    [
      teamRepsAttendance.length,
      rowItem,
      isPlayingOnHomeTeam,
      onPressManageAttendance,
    ]
  )

  return (
    <>
      {teamRepsAttendance.map((teamRep) => {
        const teamName =
          teamRepsAttendance.length > 1
            ? teamRep?.team_id === rowItem?.home_team?.id
              ? `(${rowItem?.home_team?.name})`
              : `(${rowItem?.visiting_team?.name})`
            : ''

        return (
          <button
            key={teamRep.id}
            css={manageAttendanceButtonStyle}
            onClick={() => handlePress(teamRep)}
          >
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
              <TeamOutlined css={styledTeamOutlinedStyle} />
              Manage Team Attendance
            </div>
            {teamName ? (
              <p css={manageAttendanceTextStyle}>{teamName}</p>
            ) : null}
          </button>
        )
      })}
    </>
  )
}

export default React.memo(ManageTeamAttendance)
